/**
 * Backtesting Calculation Utilities
 */

export const calculateDailyReturns = (prices) => {
  const returns = [];
  for (let i = 1; i < prices.length; i++) {
    const ret = (prices[i] - prices[i - 1]) / prices[i - 1];
    returns.push(ret);
  }
  return returns;
};

export const calculatePortfolioNAV = (historicalData, weights, initialCapital, rebalanceFrequency) => {
  const commonDates = getCommonDates(historicalData);
  
  if (commonDates.length === 0) {
    return { navSeries: [], drawdowns: [], finalValue: initialCapital };
  }
  
  const navSeries = [];
  const holdings = {};
  
  const firstDate = commonDates[0];
  Object.keys(weights).forEach(ticker => {
    const initialPrice = getPrice(historicalData[ticker], firstDate);
    if (initialPrice && initialPrice > 0) {
      holdings[ticker] = (initialCapital * weights[ticker]) / initialPrice;
    }
  });
  
  let lastRebalanceDate = firstDate;
  let peakValue = initialCapital;
  const drawdowns = [];
  
  commonDates.forEach((date, index) => {
    let currentValue = 0;
    let priceCount = 0;
    
    Object.keys(holdings).forEach(ticker => {
      const price = getPrice(historicalData[ticker], date);
      if (price && price > 0) {
        currentValue += holdings[ticker] * price;
        priceCount++;
      }
    });
    
    if (priceCount === Object.keys(holdings).length && currentValue > 0) {
      if (currentValue > peakValue) {
        peakValue = currentValue;
      }
      const drawdown = ((currentValue - peakValue) / peakValue) * 100;
      
      navSeries.push({
        date: date,
        value: currentValue,
        drawdown: drawdown
      });
      drawdowns.push(drawdown);
      
      if (shouldRebalance(date, lastRebalanceDate, rebalanceFrequency)) {
        Object.keys(weights).forEach(ticker => {
          const price = getPrice(historicalData[ticker], date);
          if (price && price > 0) {
            holdings[ticker] = (currentValue * weights[ticker]) / price;
          }
        });
        lastRebalanceDate = date;
      }
    }
  });
  
  const finalValue = navSeries.length > 0 ? navSeries[navSeries.length - 1].value : initialCapital;
  
  return { navSeries, drawdowns, finalValue };
};

const getCommonDates = (historicalData) => {
  const tickers = Object.keys(historicalData);
  
  if (tickers.length === 0) {
    return [];
  }
  
  const dateSets = tickers.map(ticker => {
    const dates = new Set();
    if (historicalData[ticker] && historicalData[ticker].prices) {
      historicalData[ticker].prices.forEach(item => {
        if (item.date) {
          dates.add(item.date);
        }
      });
    }
    return dates;
  });
  
  let commonDates = Array.from(dateSets[0]);
  
  for (let i = 1; i < dateSets.length; i++) {
    commonDates = commonDates.filter(date => dateSets[i].has(date));
  }
  
  return commonDates.sort();
};

const getPrice = (tickerData, date) => {
  if (!tickerData || !tickerData.prices) return null;
  const priceData = tickerData.prices.find(p => p.date === date);
  return priceData ? priceData.close : null;
};

const shouldRebalance = (currentDate, lastRebalanceDate, frequency) => {
  const current = new Date(currentDate);
  const last = new Date(lastRebalanceDate);
  
  const monthsDiff = (current.getFullYear() - last.getFullYear()) * 12 + 
                     (current.getMonth() - last.getMonth());
  
  switch (frequency) {
    case 'monthly':
      return monthsDiff >= 1;
    case 'quarterly':
      return monthsDiff >= 3;
    case 'annually':
      return monthsDiff >= 12;
    default:
      return false;
  }
};

export const calculateAnnualizedReturn = (initialValue, finalValue, years) => {
  if (years === 0) return 0;
  return (Math.pow(finalValue / initialValue, 1 / years) - 1) * 100;
};

export const calculateVolatility = (returns) => {
  if (returns.length === 0) return 0;
  
  const mean = returns.reduce((sum, r) => sum + r, 0) / returns.length;
  const squaredDiffs = returns.map(r => Math.pow(r - mean, 2));
  const variance = squaredDiffs.reduce((sum, sq) => sum + sq, 0) / returns.length;
  const stdDev = Math.sqrt(variance);
  
  return stdDev * Math.sqrt(252) * 100;
};

export const calculateSharpeRatio = (annualizedReturn, volatility, riskFreeRate = 4.0) => {
  if (volatility === 0) return 0;
  return (annualizedReturn - riskFreeRate) / volatility;
};

export const calculateMaxDrawdown = (drawdowns) => {
  return Math.min(...drawdowns);
};

export const calculateMonthlyStats = (navSeries) => {
  if (!navSeries || navSeries.length === 0) {
    return { winRate: 0, bestMonth: 0, worstMonth: 0, monthlyReturns: [] };
  }
  
  const monthlyData = {};
  
  navSeries.forEach(point => {
    const date = new Date(point.date);
    const monthKey = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
    
    if (!monthlyData[monthKey]) {
      monthlyData[monthKey] = { start: point.value, end: point.value };
    } else {
      monthlyData[monthKey].end = point.value;
    }
  });
  
  const monthlyReturns = [];
  const months = Object.keys(monthlyData).sort();
  
  for (let i = 1; i < months.length; i++) {
    const prevMonth = monthlyData[months[i - 1]];
    const currentMonth = monthlyData[months[i]];
    
    if (prevMonth.end > 0) {
      const monthReturn = ((currentMonth.end - prevMonth.end) / prevMonth.end) * 100;
      monthlyReturns.push(monthReturn);
    }
  }
  
  const positiveMonths = monthlyReturns.filter(r => r > 0).length;
  const winRate = monthlyReturns.length > 0 ? (positiveMonths / monthlyReturns.length) * 100 : 0;
  const bestMonth = monthlyReturns.length > 0 ? Math.max(...monthlyReturns) : 0;
  const worstMonth = monthlyReturns.length > 0 ? Math.min(...monthlyReturns) : 0;
  
  return { winRate, bestMonth, worstMonth, monthlyReturns };
};

export const calculateBenchmarkReturns = (benchmarkPrices, initialValue) => {
  if (!benchmarkPrices || benchmarkPrices.length === 0) {
    return [];
  }
  
  const initialPrice = benchmarkPrices[0].close;
  
  return benchmarkPrices.map(item => ({
    date: item.date,
    value: (item.close / initialPrice) * initialValue
  }));
};
